/**
 * sidebarSwap.js — [12.0.16] ОБМЕН ЛЕВОГО И ПРАВОГО САЙДБАРОВ МЕСТАМИ.
 *
 * Кнопка в верхнем меню (#btn-sidebar-swap) переставляет панели: серии и
 * сведения о пациенте уезжают вправо, настройки и пресеты — влево. Выбор
 * запоминается и восстанавливается при следующем запуске.
 *
 * Почему одним лишь flex order не обойтись. На десктопе (≥769px) панели —
 * накладки position:fixed, привязанные к left:0 и right:0, с разным
 * направлением выезда (translateX ±110%), скруглением и стороной тени.
 * В режиме булавки (body.sbL-pinned / sbR-pinned) они, наоборот, встают в
 * поток как position:relative — и тогда порядок решает order. Поэтому класс
 * body.sb-swapped в index.html переопределяет и то, и другое, а заодно
 * переносит ручку ширины и кромку-приманку на нужную сторону.
 *
 * Здесь же — то, что нельзя сделать стилями: запоминание выбора, значки
 * кнопок панелей (стрелка должна смотреть туда, где панель теперь выедет)
 * и пересчёт окон, потому что после перестановки изображение нужно вписать
 * заново — ровно по той же причине, что описана в htmlInit.js/_relayout.
 *
 * EXPORTS: initSidebarSwap, applySwap, isSidebarSwapped
 */

import { log } from '../utils/logger.js';

const LS_KEY = 'ui.sb.swapped';

let _bound = false;

/** Панели сейчас поменяны местами? */
export function isSidebarSwapped() {
  return document.body.classList.contains('sb-swapped');
}

function _readSaved() {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch (e) {
    return false;   // приватный режим — просто не запоминаем
  }
}

function _setIcon(btnId, href) {
  const use = document.getElementById(btnId)?.querySelector('use');
  if (!use) return;
  use.setAttribute('href', href);
  // Старые движки читают только xlink:href — в разметке проекта он не
  // используется, но если он там окажется, рассинхрона быть не должно.
  if (use.hasAttribute('xlink:href')) use.setAttribute('xlink:href', href);
}

/**
 * Применить положение панелей.
 *
 * @param {boolean} on        true — поменять местами
 * @param {Object}  [opts]
 * @param {boolean} [opts.silent] при восстановлении на старте пересчёт окон
 *                                не нужен: движок ещё сам вписывает картинку
 */
export function applySwap(on, opts = {}) {
  on = !!on;
  document.body.classList.toggle('sb-swapped', on);

  const btn = document.getElementById('btn-sidebar-swap');
  if (btn) {
    btn.classList.toggle('panel-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.title = on
      ? 'Вернуть панели на места (серии слева)'
      : 'Поменять панели местами (левая ⇄ правая)';
  }

  // Стрелка на кнопке показывает сторону, с которой панель теперь выедет
  _setIcon('btn-sidebar-left-toggle', on ? '#i-sidebar-r' : '#i-sidebar-l');
  _setIcon('btn-sidebar-right-toggle', on ? '#i-sidebar-l' : '#i-sidebar-r');

  try {
    localStorage.setItem(LS_KEY, on ? '1' : '0');
  } catch (e) { /* приватный режим */ }

  if (!opts.silent) {
    // Ширина окон та же, а вот их положение изменилось. Обычного resize мало
    // по той же причине, что и при открытии панели: движок восстановит старую
    // камеру, и изображение уедет за край. Нужно именно вписать заново.
    setTimeout(() => {
      try { window.MikhmelViewer?.relayoutViewports?.(); }
      catch (e) { /* движок ещё не поднялся — вписывать нечего */ }
      window.dispatchEvent(new Event('resize'));
    }, 320);   // после перехода панелей (0.3 с), иначе меряем старое положение
  }

  log.debug(`[SidebarSwap] Панели ${on ? 'поменяны местами' : 'на своих местах'}`);
}

export function initSidebarSwap() {
  if (_bound) return;
  _bound = true;

  // Восстановление выбора — до первого показа, чтобы панели не прыгали
  applySwap(_readSaved(), { silent: true });

  const btn = document.getElementById('btn-sidebar-swap');
  if (!btn) {
    log.warn('[SidebarSwap] Кнопки #btn-sidebar-swap нет в разметке — обмен недоступен');
    return;
  }

  btn.addEventListener('click', () => applySwap(!isSidebarSwapped()));
}
